import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { DescriptionReport, ExpenditureReport, Project, ReportType, UnifiedReport } from "../../../../../common/models/project";
import { Guid } from "guid-typescript";
import { ActivatedRoute, Router } from "@angular/router";
import { ProjectService } from "../../../../../common/services/project.service";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from "@angular/material/snack-bar";
import { forkJoin, Subject, takeUntil } from "rxjs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ProjectReportStatus } from "../../../../../common/enums/project-report-status";
import { GenericDialogComponent } from "../../../../../common/components/dialog/dialog";

@Component({
    selector: 'app-project-reports',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.scss'],
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatExpansionModule,
        MatIconModule,
        MatTooltipModule,
        MatDialogModule,
        MatSnackBarModule
    ]
})
export class ReportsComponent implements OnInit {
    private destroy$ = new Subject<void>();

    projectId: Guid | null = null;

    descriptionReports: DescriptionReport[] = [];
    expenditureReports: ExpenditureReport[] = [];


    reports: UnifiedReport[] = [];
    filteredReports: UnifiedReport[] = [];

    public ProjectReportStatus = ProjectReportStatus;

    private readonly snackbarConfig: MatSnackBarConfig = {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top'
    };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private projectService: ProjectService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const id = params.get('projectId');
            if (id && Guid.isGuid(id)) {
                this.projectId = Guid.parse(id);
                this.fetchReports();
            } 
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
      }
  

    fetchReports(): void {
        if (!this.projectId) return;

        forkJoin({
            descriptionReports: this.projectService.getDescriptionReports(this.projectId),
            expenditureReports: this.projectService.getExpenditureReports(this.projectId)
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (data) => {
                this.descriptionReports = data.descriptionReports;
                this.expenditureReports = data.expenditureReports;
                this.combineReports();
            },
            error: (err) => {
                console.error('Error fetching project reports', err);
            }
        });
    }

    applySearch(searchTerm: string): void {
        searchTerm = searchTerm.trim().toLowerCase();
        if (searchTerm) {
          this.filteredReports = this.reports.filter((report: UnifiedReport) => {
            return (
              report.name.toLowerCase().includes(searchTerm)  ||
              (report.reportingPeriodStart && report.reportingPeriodStart.toDateString().toLowerCase().includes(searchTerm)) ||
              (report.reportingPeriodEnd && report.reportingPeriodEnd.toDateString().toLowerCase().includes(searchTerm))
            );
          });
        } else {
          this.filteredReports = [...this.reports];
        }
    }

    combineReports(): void {

        const descriptionReports: UnifiedReport[] = this.descriptionReports.map(report => 
            new UnifiedReport(
                report.id,
                report.projectId,
                ReportType.Description,
                'Opisni izvještaj (Obrazac C1)',
                report.status,
                report.reportingPeriodStart,
                report.reportingPeriodEnd,
                report.submittedAt
            )
        );
    
        const expenditureReports: UnifiedReport[] = this.expenditureReports.map(report => 
            new UnifiedReport(
                report.id,
                report.projectId,
                ReportType.Expenditure,
                'Financijski izvještaj (Obrazac C2)',
                report.status,
                report.reportingPeriodStart,
                report.reportingPeriodEnd,
                report.submittedAt
            )
        );

        this.reports = [...descriptionReports, ...expenditureReports];
        this.filteredReports = [...this.reports];
    }

    goToViewReport(report: UnifiedReport): void {
        if (report.type === ReportType.Description) {
            this.router.navigate(['municipality', 'projects', this.projectId?.toString(), 'reports', report.id.toString(), 'description', 'view' ]);
        } else if (report.type === ReportType.Expenditure) {
            this.router.navigate(['municipality', 'projects', this.projectId?.toString(), 'reports', report.id.toString(), 'expenditure', 'view' ]);
        }
    }

    confirmReturn(report: UnifiedReport): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
          width: '50%',
          data: {
            title: 'Potvrdite vraćanje',
            message: `Jeste li sigurni da želite vratiti izvještaj "${report.name}" na doradu?`,
            confirmButtonText: 'Da',
            cancelButtonText: 'Ne',
          },
        });
    
        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
          if (result === true) {
            this.returnReport(report);
          }
        });
      }

      private returnReport(report: UnifiedReport): void {
        if (!this.projectId) {
            console.error('Project id must be set in order to return a project report.')
            return;
        }

        this.projectService.changeProjectReportStatus(this.projectId, report.id, report.type, { targetStatus: ProjectReportStatus.Returned }).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
              this.fetchReports();
              this.snackBar.open('Izvještaj je uspješno vraćen na doradu.', 'Zatvori', this.snackbarConfig);
            },
            error: (err) => {
              console.error('Error returning report', err);
              this.snackBar.open('Došlo je do greške pri vraćanju izvještaja na doradu.', 'Zatvori', this.snackbarConfig);
            }
          });
      } 
    
      confirmApprove(report: UnifiedReport): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
          width: '50%',
          data: {
            title: 'Potvrdite odobravanje',
            message: `Jeste li sigurni da želite odobriti izvještaj "${report.name}"?`,
            confirmButtonText: 'Da',
            cancelButtonText: 'Ne',
          },
        });
    
        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
          if (result === true) {
            this.approveReport(report);
          }
        });
      }

      private approveReport(report: UnifiedReport): void {
        if (!this.projectId) {
            console.error('Project id must be set in order to approve a project report.')
            return;
        }

        this.projectService.changeProjectReportStatus(this.projectId, report.id, report.type, { targetStatus: ProjectReportStatus.Approved }).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
              this.fetchReports();
              this.snackBar.open('Izvještaj je uspješno odobren.', 'Zatvori', this.snackbarConfig);
            },
            error: (err) => {
              console.error('Error approving report', err);
              this.snackBar.open('Došlo je do greške pri odobravanju izvještaja.', 'Zatvori', this.snackbarConfig);
            }
          });
      }
    
      confirmReject(report: UnifiedReport): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
          width: '50%',
          data: {
            title: 'Potvrdite odbijanje',
            message: `Jeste li sigurni da želite odbiti izvještaj "${report.name}"?`,
            confirmButtonText: 'Da',
            cancelButtonText: 'Ne',
          },
        });
    
        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
          if (result === true) {
            this.rejectReport(report);
          }
        });
      }

      private rejectReport(report: UnifiedReport): void {
        if (!this.projectId) {
            console.error('Project id must be set in order to reject a project report.')
            return;
        }

        this.projectService.changeProjectReportStatus(this.projectId, report.id, report.type, { targetStatus: ProjectReportStatus.Rejected }).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
              this.fetchReports();
              this.snackBar.open('Izvještaj je uspješno odbijen.', 'Zatvori', this.snackbarConfig);
            },
            error: (err) => {
              console.error('Error rejecting report', err);
              this.snackBar.open('Došlo je do greške pri odbijanju izvještaja.', 'Zatvori', this.snackbarConfig);
            }
          });
      } 

      downloadReport(report: UnifiedReport): void {
        if (!this.projectId) {
            console.error('Project id must be set in order to download a project report.')
            return;
        }

        this.projectService.downloadReport(this.projectId, report.id, report.type).pipe(takeUntil(this.destroy$)).subscribe({
            next: (blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = report.name + this.getFileExtension(report.type);
                a.click();
                window.URL.revokeObjectURL(url);
                // this.snackBar.open('Izvještaj je preuzet.', 'Close', { duration: 3000 });
            },
            error: (err) => {
                console.error('Error downloading report', err);
            }
        });
    }

    private getFileExtension(type: ReportType): string {
        switch (type) {
            case ReportType.Description:
                return '.docx';
            case ReportType.Expenditure:
                return '.xls';
            default:
                return '';
        }
    }
}
